import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditCalendar } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type {
  Planbook,
  PlanbookDateAnnotationDateRange,
  PlanbookDateAnnotationSingle,
} from "@shared/models/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash"
import moment from "moment"
import { findPlanbookDatesOff } from "../../../dates-off-calculator"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  dateAnnotation: PlanbookDateAnnotationSingle | PlanbookDateAnnotationDateRange
  planbook: Planbook
  bumpDeleteOrNothing: "BUMP" | "DELETE" | "NOTHING"
  bumpDeleteOrNothingPatches: Array<PatchModel>
  bumpDeleteOrNothingUndoPatches: Array<PatchModel>
}

export default function preparePlanbookAddSpecialEvent(payload: Payload): PreparedAction {
  return {
    name: "PLANBOOK_DATE_ANNOTATION_ADD_SPECIAL_EVENT",
    payload: payload,
    patches: payload.bumpDeleteOrNothingPatches.concat(patches(payload)),
    undoPatches: payload.bumpDeleteOrNothingUndoPatches.concat(undoPatches(payload)),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let newAnnotations = payload.planbook.attributes.calendar.dateAnnotations.concat([
    payload.dateAnnotation,
  ])
  let allDatesOff = findPlanbookDatesOff(newAnnotations)

  let patch = Patch.create("planbook", payload.planbook.id)
    .push("attributes.calendar.dateAnnotations", payload.dateAnnotation)
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  let oldAnnotations = JSON.parse(
    JSON.stringify(payload.planbook.attributes.calendar.dateAnnotations)
  )
  let allDatesOff = findPlanbookDatesOff(oldAnnotations)

  let patch = Patch.create("planbook", payload.planbook.id)
    .pull("attributes.calendar.dateAnnotations", {
      id: payload.dateAnnotation.id,
    })
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let title, description: string
  if (payload.dateAnnotation.attributes.isOff) {
    if (payload.dateAnnotation.type === "date-annotation-planbook-custom-single") {
      title = "Added A No School Day"
      description =
        "Turned off " +
        moment(payload.dateAnnotation.attributes.dateString).format("ddd, MMM. D, YYYY")
    } else {
      title = "Added No School Days"
      const formattedStartDate = moment(payload.dateAnnotation.attributes.startDate).format(
        "ddd, MMM. D, YYYY"
      )
      const formattedEndDate = moment(payload.dateAnnotation.attributes.endDate).format(
        "ddd, MMM. D, YYYY"
      )
      description = `Turned off days from ${formattedStartDate} to ${formattedEndDate}`
    }

    if (payload.bumpDeleteOrNothingPatches.length > 0) {
      switch (payload.bumpDeleteOrNothing) {
        case "BUMP":
          description += " and bumped existing lessons"
          break
        case "DELETE":
          description += " and erased existing lessons"
          break
      }
    }
  } else {
    title = "Added Special Event"
    let descriptionStarter = "Added special event"
    let descriptionPhrase =
      payload.dateAnnotation.type === "date-annotation-planbook-custom-single"
        ? `on ${moment(payload.dateAnnotation.attributes.dateString).format("ddd, MMM. D, YYYY")}`
        : `from ${moment(payload.dateAnnotation.attributes.startDate).format(
            "ddd, MMM. D, YYYY"
          )} to ${moment(payload.dateAnnotation.attributes.endDate).format("ddd, MMM. D, YYYY")}`
    description = `${descriptionStarter} ${descriptionPhrase}`
  }

  let titleForAnalytics =
    payload.dateAnnotation.type === "date-annotation-planbook-custom-single"
      ? "Planbook Date - Create Annotation - Single"
      : "Planbook Date - Create Annotation - Range"

  let newEvent: PlanbookEditCalendar = {
    ...createBlankEvent(),
    root_id: payload.planbook.id,
    root_type: "planbook",
    primary_object_type: "planbook",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
    primary_object_id_and_ancestor_ids: [payload.planbook.id],
    primary_verb: "edit",
    primary_object_specifier: "calendar",
    adverbial_verb: "adding",
    adverbial_object_type: "annotation",
    adverbial_object_specifier:
      payload.dateAnnotation.type === "date-annotation-planbook-custom-single" ? "single" : "range",
  }

  return {
    title,
    titleForAnalytics: titleForAnalytics,
    description,
    activity: {},
    context: {
      noSchool: payload.dateAnnotation.attributes.isOff,
      planbookId: payload.planbook.id,
      dateAnnotationId: payload.dateAnnotation.id,
      bumpDeleteOrNothing: payload.bumpDeleteOrNothing,
    },
    newAnalyticsEvents: [newEvent],
  }
}
